import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { fromJS } from 'immutable';
import PropTypes from 'prop-types';

import { getPopupRegistration, openPopup, Priority } from 'app/PopupHolder';
import Confirm, { type } from 'app/components/Confirm';
import Modal from 'components/base/Modal';
import Button, { SolidButton } from 'components/Button';
import { selectLoading } from 'data/property';
import { selectAvailableListingTypes } from 'data/search';
import { TagFilterProvider } from 'app/Search/Header/searchFilterNew/TagFilterContext';

import { ActiveSectionProvider } from './panel/ActiveSectionContext';
import { FilterStateProvider, useFilterState } from './state/FilterStateContext';
import { MainPanel } from './panel/MainPanel';
import CustomSummarySection from './CustomSummarySection';
import { CautionBanner, CautionOverlay } from './CautionOverlay';
import css from './CustomFilterModal.scss';

/*
 * Redesigned replacement for the legacy SearchFilterViewerPopup on the My
 * Properties screen. Renders the new search-filter panel UI (copied from
 * app/Search/Header/searchFilterNew and wired to a private, modal-scoped
 * filter store) in two modes:
 *
 *  - mode="summary": summary filter card editing (Custom Summary panel +
 *    Apply / Reset / optional Delete Filter). Output contract matches the
 *    legacy popup: onSave(searchImmutable, closePopup).
 *  - mode="list": automated list filter editing (caution overlay first, then
 *    a warning banner + Save List).
 */

const ModalBody = ({ mode, editEnabled, search, onSave, onDelete, closePopup, loading, confirm, alert }) => {
  const { slice, initialSlice, reset } = useFilterState();
  const summaryMode = mode !== 'list';

  const [name, setName] = useState(summaryMode ? search.name || '' : '');
  const [description, setDescription] = useState(summaryMode ? search.description || '' : '');
  const [defaultFilter, setDefaultFilter] = useState(summaryMode ? !!search.defaultFilter : false);
  const [confirmed, setConfirmed] = useState(summaryMode);

  const handleApply = () => {
    const filterOut = slice.get('filter').toJS();
    const hasFilters = Object.keys(filterOut).some(key => key !== 'estimatedValueGrowthPeriod' && filterOut[key] != null);

    // Parity with the legacy editor's summary-mode validation.
    if (summaryMode && !hasFilters) {
      alert('Please apply at least one filter.');
      return;
    }

    // Keys the whitelisted filter never carries (record identity, location,
    // legacy-only fields) must ride through unchanged so the PUT updates the
    // same record the legacy popup would have updated.
    const seededKeys = initialSlice.get('filter').keySeq().toSet();
    const passthrough = Object.keys(search)
      .filter(key => !seededKeys.has(key) && !(key in filterOut) && !['name', 'description', 'defaultFilter'].includes(key))
      .reduce((memo, key) => ({ ...memo, [key]: search[key] }), {});

    const out = fromJS({
      ...passthrough,
      ...filterOut,
      ...(summaryMode
        ? { name: name || null, description: description || null, defaultFilter: !!defaultFilter }
        : { name: search.name, description: search.description }),
    });

    // Same contract as the legacy SearchFilterViewer: the caller does
    // search.toJS(), normalizes listingType, and adds filterType/group id.
    onSave(out, closePopup);
  };

  const handleReset = () => {
    reset();
    if (summaryMode) {
      setName(search.name || '');
      setDescription(search.description || '');
      setDefaultFilter(!!search.defaultFilter);
    }
  };

  const handleDelete = () => {
    confirm({
      caption: 'Confirm Delete',
      onOk: () => onDelete(closePopup),
      okLabel: 'Delete',
      type: type.danger,
      question: 'Delete your custom filter and restore the original?',
    });
  };

  // Reset is only meaningful once something has actually diverged from the
  // seeded state — otherwise there's nothing to revert.
  const hasChanges = !slice.get('filter').equals(initialSlice.get('filter')) || (summaryMode && (
    name !== (search.name || '')
    || description !== (search.description || '')
    || defaultFilter !== !!search.defaultFilter
  ));

  const btnProps = { loading, disabled: !confirmed };
  const resetBtnProps = { loading, disabled: !confirmed || !hasChanges };

  return (
    <div className={css.container}>
      <div className={css.header}>
        <div className={css.title}>
          {summaryMode ? `Current Summary Filters: ${search.name || '<New>'}` : 'Current List Search Filters'}
        </div>
        <div className={css.buttons}>
          <Button loading={loading} disabled={loading} className={css.cancelBtn} onClick={closePopup}>Cancel</Button>
          <Button {...resetBtnProps} className={css.resetBtn} onClick={handleReset}>Reset</Button>
          {summaryMode && onDelete ? <Button {...btnProps} className={css.deleteButton} onClick={handleDelete}>Delete Filter</Button> : null}
          <SolidButton {...btnProps} className={css.applyBtn} onClick={handleApply}>{summaryMode ? 'Apply' : 'Save List'}</SolidButton>
        </div>
      </div>
      {summaryMode ? null : <CautionBanner />}
      <div className={css.body}>
        <MainPanel
          hotkeysEnabled={summaryMode || confirmed}
          topLeftContent={!summaryMode ? null : (
            <CustomSummarySection
              name={name}
              description={description}
              defaultFilter={defaultFilter}
              onNameChange={setName}
              onDescriptionChange={setDescription}
              onDefaultFilterChange={setDefaultFilter}
            />
          )}
        />
      </div>
      {summaryMode || confirmed ? null : (
        <CautionOverlay
          editEnabled={editEnabled}
          loading={loading}
          onCancel={closePopup}
          onEdit={() => setConfirmed(true)}
        />
      )}
    </div>
  );
};

ModalBody.propTypes = {
  mode: PropTypes.oneOf(['summary', 'list']),
  editEnabled: PropTypes.bool,
  search: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  closePopup: PropTypes.func.isRequired,
};

const CustomFilterModal = ({ search, mode = 'summary', availableListingTypes, leadListOptions, closePopup, ...rest }) => {
  // Group filters arrive as plain objects; guard against Immutable input.
  const plainSearch = useMemo(
    () => (search && typeof search.toJS === 'function' ? search.toJS() : search || {}),
    [search],
  );

  return (
    <Modal isOpen uniqId="customFilterModalPopup" padding="0" width="1280px" maxWidth="96vw" height="700px" maxHeight="92vh" onClose={closePopup}>
      <FilterStateProvider seedSearch={plainSearch} availableListingTypes={availableListingTypes} leadListOptions={leadListOptions}>
        <TagFilterProvider>
          <ActiveSectionProvider>
            <ModalBody {...rest} mode={mode} search={plainSearch} closePopup={closePopup} />
          </ActiveSectionProvider>
        </TagFilterProvider>
      </FilterStateProvider>
    </Modal>
  );
};

CustomFilterModal.propTypes = {
  search: PropTypes.object,
  mode: PropTypes.oneOf(['summary', 'list']),
  editEnabled: PropTypes.bool,
  leadListOptions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

const CustomFilterModalPopup = connect(state => ({
  loading: selectLoading(state),
  availableListingTypes: selectAvailableListingTypes(state),
}), {
  confirm: Confirm.open,
  alert: Confirm.alert,
})(CustomFilterModal);

const registrationId = getPopupRegistration(CustomFilterModalPopup);
CustomFilterModalPopup.open = props => openPopup(registrationId, { ...props, priority: Priority.MEDIUM });

export default CustomFilterModalPopup;
